import { useApolloClient } from "@apollo/client/react";
import { debounce, get, split, without } from "lodash";
import { useCallback, useMemo, useState } from "react";

import { DEBOUNCE_SEARCH_TIME } from "src/consts/global";

/**
 * Debounced async-select loader for people search.
 *
 * @param {{
 *   query: import("@apollo/client").DocumentNode,
 *   mapPeopleToOptions: (people: unknown[]) => unknown[],
 *   fetchPolicy?: string,
 *   extraVariables?: Record<string, unknown>,
 * }} config
 */
export const useDebouncedPersonSearch = ({
  query,
  mapPeopleToOptions,
  fetchPolicy = "no-cache",
  extraVariables,
} = {}) => {
  const client = useApolloClient();
  const [isSearching, setIsSearching] = useState(false);

  const getPersonQueryVars = useCallback(
    (keywordArray = "") => ({
      searchTerms: without(split(keywordArray, " "), "") || [keywordArray],
      size: 10,
      ...extraVariables,
    }),
    [extraVariables]
  );

  const loadPersonOptions = useMemo(
    () =>
      debounce((input, callback) => {
        setIsSearching(true);
        client
          .query({
            query,
            variables: getPersonQueryVars(input),
            fetchPolicy,
          })
          .then(({ data, error }) => {
            if (error) {
              return;
            }

            callback(mapPeopleToOptions(get(data, "result.people", [])));
            setIsSearching(false);
          });
      }, DEBOUNCE_SEARCH_TIME),
    [client, fetchPolicy, getPersonQueryVars, mapPeopleToOptions, query]
  );

  return {
    loadPersonOptions,
    isSearching,
    client,
  };
};
